// @ts-nocheck
import React, { FC, useContext, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Col,
  ConfigProvider,
  Form,
  Input,
  Modal,
  Row,
  Space,
  Typography,
} from "antd";
import { SafetyOutlined, WarningOutlined } from "@ant-design/icons";
import locale from "antd/locale/fa_IR";
import { captureException } from "@sentry/react";
import useHttpClient from "../../hooks/useHttpClient";
import { filterToDigits, isPhoneNumberValid, toLatinDigit } from "../../utils/digits";
import { AppContext } from "../../constants/contexts";
import OTPModal from "../OTPModal";
import { AxiosError } from "axios";
import { getErrorMessage } from "../../utils/http";

function nationalCodeValidator(c) {
  const code = toLatinDigit(c);
  if (code.length !== 10 || /(\d)(\1){9}/.test(code)) return false;

  let sum = 0;
  const chars = code.split("");

  for (let i = 0; i < 9; i++) sum += +chars[i] * (10 - i);

  const remainder = sum % 11;
  const lastDigit = remainder < 2 ? remainder : 11 - remainder;

  return +chars[9] === lastDigit;
}

const InfoEdit: FC<{ data: any; onCancel: () => void; onSuccess: () => void }> = ({
  data,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const httpClient = useHttpClient();
  const [loading, setLoading] = useState(false);
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [pendingPhoneNumber, setPendingPhoneNumber] = useState<string | null>(null);
  const { messageApi } = useContext(AppContext);

  const phoneNumber = Form.useWatch("phoneNumber", form);
  const postalCode = Form.useWatch("postalCode", form);
  const nationalCode = Form.useWatch("nationalCode", form);
  const birthDate = Form.useWatch("birthDate", form);
  const firstNameEnglish = Form.useWatch("firstNameEnglish", form);
  const lastNameEnglish = Form.useWatch("lastNameEnglish", form);

  useEffect(() => {
    const initialValues = {
      phoneNumber: data?.phoneNumber || "",
      postalCode: data?.postalCode || "",
      firstNameEnglish: data?.information?.firstNameEnglish || "",
      lastNameEnglish: data?.information?.lastNameEnglish || "",
    };

    // Only include nationalCode and birthDate if not verified
    if (!data?.isNationalCodeVerified) {
      let birthDateValue = null;

      initialValues.nationalCode = data?.nationalCode || "";
      initialValues.birthDate = data.birthDate || "";
    }

    form.setFieldsValue(initialValues);
  }, [data, form]);

  function serializeForm() {
    const values = form.getFieldsValue();
    const information = {
      postalCode: values.postalCode,
      firstNameEnglish: values.firstNameEnglish,
      lastNameEnglish: values.lastNameEnglish,
    };

    // Only include nationalCode and birthDate if not verified
    if (!data?.isNationalCodeVerified) {
      information.nationalCode = filterToDigits(values.nationalCode);
      information.birthDate = values.birthDate ? toLatinDigit(values.birthDate) : undefined;
    }

    const payload: any = {
      ...information,
    };

    return payload;
  }

  async function updatePhoneNumber() {
    setLoading(true);
    try {
      const values = form.getFieldsValue();
      const phoneNumber = filterToDigits(values.phoneNumber);
      const { data } = await httpClient.put("/users/me/phone/", { phoneNumber });
      setPendingPhoneNumber(phoneNumber);
      setOtpModalOpen(true);
      messageApi.open({
        type: "info",
        content: "کد تایید به شماره همراه شما ارسال شد",
      });
    } catch (error: any) {
      captureException(error);
      const errorData = error?.response?.data;
      // If an active OTP already exists, still open the OTP modal
      if (errorData?.error === "An active OTP already exists for this type.") {
        const values = form.getFieldsValue();
        const phoneNumber = filterToDigits(values.phoneNumber);
        setPendingPhoneNumber(phoneNumber);
        setOtpModalOpen(true);
        const errorMessage = getErrorMessage(
          error,
          "کد تایید فعالی وجود دارد. لطفاً از کد قبلی استفاده کنید."
        );
        messageApi.open({
          type: "info",
          content: errorMessage,
        });
      } else {
        const errorMessage = getErrorMessage(error, "خطا در ارسال کد تایید");
        messageApi.open({
          type: "error",
          content: errorMessage,
        });
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit() {
    const values = form.getFieldsValue();
    const phoneNumberChanged =
      filterToDigits(values.phoneNumber) !== filterToDigits(data?.phoneNumber || "");

    // If phone number changed, first update phone and show OTP modal
    if (!data.isPhoneVerified || phoneNumberChanged) {
      return updatePhoneNumber();
    } else {
      sendUpdate();
    }
  }

  async function sendUpdate() {
    // If phone number didn't change, proceed with normal update
    setLoading(true);
    try {
      await httpClient.put("/users/me/information", serializeForm(true));
      messageApi.open({
        type: "success",
        content: "تغییرات ذخیره شد",
      });
      if (onSuccess) onSuccess();
    } catch (error: AxiosError<{ error: string; status: number }>) {
      const errorMessage = getErrorMessage(error, "خطا در ذخیره تغییرات");
      messageApi.open({
        type: "error",
        content: errorMessage,
      });
      captureException(error);
    } finally {
      setLoading(false);
    }
  }

  async function handleOTPSuccess() {
    // After OTP verification, update all user info including phoneNumber
    setLoading(true);
    sendUpdate();
  }

  function submitEnabled() {
    const values = form.getFieldsValue();

    if (values.phoneNumber && !data.isPhoneVerified) {
      return true;
    }

    const currentPhone = filterToDigits((values.phoneNumber || "").trim());
    const originalPhone = filterToDigits((data?.phoneNumber || "").trim());
    if (currentPhone !== originalPhone) {
      return true;
    }

    // Compare postalCode
    const currentPostalCode = (values.postalCode || "").trim();
    const originalPostalCode = (data?.postalCode || "").trim();
    if (currentPostalCode !== originalPostalCode) {
      return true;
    }

    // Compare firstNameEnglish
    const currentFirstNameEnglish = (values.firstNameEnglish || "").trim();
    const originalFirstNameEnglish = (data?.information?.firstNameEnglish || "").trim();
    if (currentFirstNameEnglish !== originalFirstNameEnglish) {
      return true;
    }

    // Compare lastNameEnglish
    const currentLastNameEnglish = (values.lastNameEnglish || "").trim();
    const originalLastNameEnglish = (data?.information?.lastNameEnglish || "").trim();
    if (currentLastNameEnglish !== originalLastNameEnglish) {
      return true;
    }

    // Compare nationalCode and birthDate (if editable)
    if (!data.isNationalCodeVerified) {
      const currentNationalCode = filterToDigits((values.nationalCode || "").trim());
      const originalNationalCode = filterToDigits((data?.nationalCode || "").trim());
      if (currentNationalCode !== originalNationalCode) {
        return true;
      }

      const currentBirthDate = toLatinDigit((values.birthDate || "").trim());
      const originalBirthDate = toLatinDigit((data?.birthDate || "").trim());
      if (currentBirthDate !== originalBirthDate) {
        return true;
      }
    }

    return false;
  }
  return (
    <>
      <OTPModal
        type="PHONE_VERIFICATION"
        phoneNumber={pendingPhoneNumber}
        open={otpModalOpen}
        onClose={() => {
          setOtpModalOpen(false);
          setPendingPhoneNumber(null);
        }}
        onSuccess={handleOTPSuccess}
        onResend={updatePhoneNumber}
      />
      <Space className="mt-4 pb-0! w-full h-full" direction="vertical" size="middle">
        <ConfigProvider locale={locale}>
          <Form form={form} layout="vertical"
                  variant='filled'

            onFinish={handleSubmit}>
            <Form.Item
              label="شماره همراه"
              name="phoneNumber"
              rules={[
                { required: true, message: "این فیلد ضروری است" },
                {
                  message: "شماره همراه معتبر نیست",
                  validator: (_, value) =>
                    !isPhoneNumberValid(value) ? Promise.reject() : Promise.resolve(),
                },
              ]}
            >
              <Input style={{ direction: "rtl" }} />
            </Form.Item>
            {(!data.information?.nationalCode || !data.isNationalCodeVerified) && (
              <>
                <Form.Item
                  label="کد ملی"
                  name="nationalCode"
                  rules={[
                    { required: true, message: "این فیلد ضروری است" },
                    {
                      message: "کد ملی معتبر نیست",
                      validator: (_, value) =>
                        !nationalCodeValidator(value) ? Promise.reject() : Promise.resolve(),
                    },
                  ]}
                >
                  <Input style={{ direction: "rtl" }} />
                </Form.Item>
                <Form.Item
                  rules={[
                    { required: true, message: "این فیلد ضروری است" },
                    {
                      pattern:
                        /^[\d\u06F0-\u06F9\u0660-\u0669]{4}\/[\d\u06F0-\u06F9\u0660-\u0669]{2}\/[\d\u06F0-\u06F9\u0660-\u0669]{2}$/,
                      message: "فرمت تاریخ باید به صورت YYYY/MM/DD باشد",
                    },
                  ]}
                  label="تاریخ تولد"
                  name="birthDate"
                >
                  <Input placeholder="1370/01/01" style={{ direction: "ltr" }} />
                </Form.Item>
              </>
            )}
            {/* <Form.Item
              label="نام به انگلیسی"
              name="firstNameEnglish"
              rules={[{ required: true, message: "این فیلد ضروری است" }]}
            >
              <Input style={{ direction: "rtl" }} />
            </Form.Item>
            <Form.Item
              label="نام‌خانوادگی به انگلیسی"
              name="lastNameEnglish"
              rules={[{ required: true, message: "این فیلد ضروری است" }]}
            >
              <Input style={{ direction: "rtl" }} />
            </Form.Item> */}
            <Form.Item
              label="کد پستی"
              name="postalCode"
              rules={[]}
            >
              <Input style={{ direction: "rtl" }} />
            </Form.Item>
            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  disabled={!submitEnabled()}
                >
                  ذخیره
                </Button>
                <Button onClick={onCancel}>لغو</Button>
              </Space>
            </Form.Item>
          </Form>
        </ConfigProvider>
      </Space>
    </>
  );
};

export default InfoEdit;
